import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { loadEstateOwnedFilesRoutine } from 'ducks/estateDocuments/actions'
import { getFileTypes } from 'ducks/files/selectors'
import { getApiErrors } from 'utils/errors'
import { font, ink, line, radius, surface, transition } from 'features/dashboard/tokens'
import { fileUpdateMessage } from 'utils/fileUpdateMessage'

const CYAN = '#33becc'

const EstateFileRenameModal = ({ estateId, file, folderId, onClose }) => {
  const dispatch = useDispatch()
  const fileTypesRaw = useSelector(getFileTypes)
  const currentName = file.displayName || file.display_name || file.name || ''
  const [displayName, setDisplayName] = useState(currentName)
  const [description, setDescription] = useState(file.description || '')
  const [fileTypeId, setFileTypeId] = useState(file.fileType?.id || file.fileTypeId || '')
  const [saving, setSaving] = useState(false)

  const fileTypeOptions = useMemo(() => {
    const list = Array.isArray(fileTypesRaw) ? fileTypesRaw : []
    return [...list]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(type => ({ value: type.id, label: type.name }))
  }, [fileTypesRaw])

  const handleSubmit = async () => {
    const trimmed = displayName.trim()
    if (!trimmed || saving) return
    setSaving(true)
    try {
      await estateDocumentsService.updateEstateFile({
        estateId,
        fileId: file.id,
        displayName: trimmed,
        description: description.trim(),
        fileTypeId: fileTypeId || null
      })
      toast.success(fileUpdateMessage({ displayName: trimmed }, currentName))
      dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId, page: 1, reloadLoaded: true }))
      onClose()
    } catch (error) {
      const apiError = getApiErrors(error)
      if (apiError?.trim()) toast.error(apiError)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>Edit document details</Title>
        <Field>
          <FieldLabel htmlFor='estate-file-display-name'>Document name</FieldLabel>
          <TextInput
            id='estate-file-display-name'
            autoFocus
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleSubmit()
              if (event.key === 'Escape') onClose()
            }}
            placeholder='e.g. Fire risk assessment.pdf'
            disabled={saving}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor='estate-file-description'>Description</FieldLabel>
          <TextArea
            id='estate-file-description'
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder='Add a short description (optional)'
            disabled={saving}
            rows={3}
          />
        </Field>
        {fileTypeOptions.length > 0 && (
          <Field>
            <FieldLabel htmlFor='estate-file-type'>Document type</FieldLabel>
            <SearchableSelect
              id='estate-file-type'
              value={fileTypeId}
              onChange={setFileTypeId}
              options={fileTypeOptions}
              clearable
              clearLabel='Unclassified'
              placeholder='Unclassified'
              searchPlaceholder='Search document types…'
              ariaLabel='Document type'
              disabled={saving}
            />
          </Field>
        )}
        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={saving}>
            Cancel
          </CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={saving || !displayName.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </SaveButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateFileRenameModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const TextInput = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  outline: none;
  box-sizing: border-box;
  transition: ${transition};

  &:focus {
    border-color: ${CYAN};
  }

  &::placeholder {
    color: ${ink.mute};
  }

  &:disabled {
    opacity: 0.6;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  transition: ${transition};

  &:focus {
    border-color: ${CYAN};
  }

  &::placeholder {
    color: ${ink.mute};
  }

  &:disabled {
    opacity: 0.6;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
